<template>
  <form @submit.prevent="submitData">
    <div>
      <TextInput
        id="ownership-name"
        label="Name*"
        :val="name"
        type="text"
        autofocus
        :required="true"
        @input="name = $event"
      />
    </div>
    <div class="mt-3">
      <label class="text-sm font-semibold">Labels</label>
      <MultiSelector
        ref="labelsCombobox"
        class="mt-3"
        :options="labels"
        :options-to-edit="selectedLabels"
        display-prop="label_name"
        value-prop="id"
        @values-updated="updateLabels"
      />
    </div>
    <div class="mt-3">
      <label class="text-sm font-semibold">Location*</label>
      <AutoCompleteInput
        class="mt-3"
        :model="selectedRegion"
        :items="regions"
        placeholder="Start typing to view regions..."
        display-key="region_name"
        value-key="region_code"
        @input="selectedRegion = $event"
      />
      <span
        v-show="!isValidRegion"
        class="text-xs text-red-700"
      >Please enter a valid region</span>
    </div>
    <div class="mt-3">
      <TextInput
        id="ownership-amt"
        label="% Ownership*"
        :val="ownershipAmt"
        min="0"
        type="number"
        max="100"
        step="0.01"
        autofocus
        :required="true"
        @input="ownershipAmt = $event"
      />
    </div>
    <div class="mt-3">
      <TextareaInput
        label="Description"
        :val="description"
        placeholder="Describe this source..."
        :required="true"
        @input="description = $event"
      />
    </div>
    <p class="my-2 text-sm font-semibold">
      Add the emission types relevant to this source
    </p>
    <div class="p-2 mb-3 bg-gray-100">
      <div class="mt-3">
        <label
          for="ownership"
          class="block mb-3 text-sm font-semibold"
        >Ownership*</label>
        <DropDown
          :model="selectedOwnership"
          display-key="title"
          value-key="id"
          :options="ownerships"
          @value-updated="updateOwnership"
        />
      </div>

      <div class="mt-3">
        <label
          for="group"
          class="block mb-3 text-sm font-semibold"
        >Group*</label>
        <DropDown
          :model="selectedGroup"
          display-key="name"
          value-key="id"
          :options="selectedGroups"
          @value-updated="updateGroup"
        />
      </div>
      <section
        id="category-types"
        class="flex flex-col items-end gap-2 mt-6 md:flex-row md:gap-6"
      >
        <div class="w-full">
          <span class="block mb-2 text-sm font-semibold">Category*</span>
          <DropDown
            :model="selectedCategory"
            display-key="name"
            value-key="id"
            :options="selectedCategories"
            @value-updated="updateCategory"
          />
        </div>
        <div class="w-full">
          <span class="block mb-2 text-sm font-semibold">Type*</span>
          <DropDown
            :model="selectedType"
            display-key="name"
            value-key="id"
            :options="selectedTypes"
            @value-updated="updateType"
          />
        </div>
        <div class="w-full">
          <button
            class="w-full px-4 py-2 mt-2 text-sm border shadow-sm focus:shadow-outline border-main-primary text-emerald-700 hover:bg-emerald-700 hover:text-white focus:outline-none"
            type="button"
            @click="addCategoryType"
          >
            Add
          </button>
        </div>
      </section>

      <section
        v-if="categoryTypes.length"
        id="selected-category-types"
        class="flex flex-col gap-2 mt-4"
      >
        <span class="text-sm font-semibold">Types</span>
        <div
          v-for="({ category, type }, index) in categoryTypes"
          :key="index"
          class="flex items-center gap-2 flex-nowrap"
        >
          <div class="grow rounded-md bg-[grey] px-3 py-1 text-left text-base font-light text-white">
            {{ category.name }} : {{ type.name }}
          </div>
          <button
            type="button"
            class="p-0 outline-none"
            @click="removeCategoryType(index)"
          >
            <TimesSmall />
          </button>
        </div>
      </section>
    </div>
    <PrimaryButton
      type="submit"
      :disabled="cantSubmitData"
    >
      Save Source
    </PrimaryButton>
  </form>
</template>

<script>
export default {
  name: 'SourceForm',

  props: {
    buttonText: {
      type: String,
      default: 'Save source',
    },
    sourceData: {
      type: Object,
      default: () => {
        return {
          name: '',
          description: '',
          selectedLabels: [],
          categoryTypes: [],
          ownershipAmt: 0,
          selectedRegion: '',
        }
      },
    },
  },

  emits: ['sourceDataSubmitted'],

  data() {
    const {
      name,
      description,
      categoryTypes,
      selectedLabels,
      selectedRegion,
      ownershipAmt,
    } = this.sourceData
    return {
      name,
      description,
      labels: [],
      selectedLabels,
      selectedRegion,
      ownershipAmt,
      updatedLabels: [],
      timesLabelsChanged: 0,
      ownerships: [],
      selectedOwnership: null,
      groups: [],
      selectedGroups: [],
      selectedGroup: null,
      categories: [],
      selectedCategories: [],
      selectedCategory: null,
      types: [],
      regions: [],
      selectedTypes: [],
      selectedType: null,
      categoryTypes,
    }
  },

  computed: {
    cantSubmitData() {
      return !this.name || !this.categoryTypes.length || !this.isValidRegion
    },
    isValidRegion() {
      const region = this.selectedRegion
        ? this.selectedRegion.region_code
        : this.selectedRegion
      return this.regions.some(r => r.region_code === region)
    },
  },

  mounted() {
    const cid = localStorage.getItem('cid')
    const { listCategories, listTypes, listOwnerships, listGroups }
      = this.$apis.emissionTracker

    Promise.all([
      listCategories(),
      listTypes(),
      listOwnerships(),
      listGroups(),
      this.$apis.companies.listCompanyLabels(cid),
      this.$apis.efdb.listRegions(),
    ]).then((responses) => {
      const [
        { data: categoriesData },
        { data: typesData },
        { data: ownershipsData },
        { data: groupsData },
        { data: labelsData },
        { data: regionsData },
      ] = responses

      this.labels = labelsData.results
      this.regions = regionsData

      this.ownerships = ownershipsData
      this.selectedOwnership = ownershipsData[0].id

      this.groups = groupsData
      this.filterGroups()

      this.categories = categoriesData
      this.filterCategories()

      this.types = typesData
      this.filterTypes()

      this.categoryTypes.map((catType) => {
        catType.category = this.categories.find(
          ({ id }) => id === catType.category.id,
        )
        catType.type = this.types.find(({ id }) => id === catType.type.id)
        return catType
      })
    })
  },

  methods: {
    updateLabels(labels) {
      this.timesLabelsChanged++
      this.updatedLabels = labels
    },
    updateOwnership(value) {
      this.selectedOwnership = Number(value)
      this.filterGroups()
      this.filterCategories()
      this.filterTypes()
    },
    updateGroup(value) {
      this.selectedGroup = Number(value)
      this.filterCategories()
      this.filterTypes()
    },
    updateCategory(value) {
      this.selectedCategory = Number(value)
      this.filterTypes()
    },
    updateType(value) {
      this.selectedType = Number(value)
    },
    filterGroups() {
      this.selectedGroups = this.groups.filter(({ ownership }) =>
        ownership.some(({ id }) => id === this.selectedOwnership),
      )
      this.selectedGroup = this.selectedGroups[0].id
    },
    filterCategories() {
      const { title } = this.ownerships.find(
        ({ id }) => id === parseInt(this.selectedOwnership),
      )
      this.selectedCategories = this.categories.filter(
        ({ group, ownership }) =>
          group.some(({ id }) => id === this.selectedGroup)
          && ownership === title,
      )
      this.selectedCategory = this.selectedCategories[0].id
    },
    filterTypes() {
      this.selectedTypes = this.sortedOptions(
        this.types.filter(({ category }) =>
          category.some(({ id }) => id === this.selectedCategory),
        ),
      )
      this.selectedType = this.selectedTypes[0].id
    },
    addCategoryType() {
      let categoryTypesExists = false
      this.categoryTypes.every(({ category, type }) => {
        if (
          category.id === this.selectedCategory
          && type.id === this.selectedType
        ) {
          categoryTypesExists = true
          return false
        }
        return true
      })
      if (categoryTypesExists) return
      const category = this.categories.find(
        cat => cat.id === this.selectedCategory,
      )
      const type = this.types.find(type => type.id === this.selectedType)
      this.categoryTypes.push({ category, type })
    },
    removeCategoryType(index) {
      this.categoryTypes.splice(index, 1)
    },
    submitData() {
      const data = {
        company: Number(localStorage.getItem('cid')),
        name: this.name,
        ownership: this.selectedOwnership,
        group: this.selectedGroup,
        description: this.description ?? '',
        labels: this.timesLabelsChanged
          ? this.updatedLabels
          : this.selectedLabels.map(({ id }) => id),
        category_types: [...this.categoryTypes].map(({ category, type }) => {
          return {
            category: category.id,
            type: type.id,
          }
        }),
        ownership_percentage: this.ownershipAmt,
        location: this.selectedRegion.region_code
          ? this.selectedRegion.region_code
          : this.selectedRegion,
      }
      this.$emit('sourceDataSubmitted', data)
    },
    sortedOptions(options) {
      return options.sort((a, b) => a.name.localeCompare(b.name))
    },
  },
}
</script>
